import { cx } from "@emotion/css";
import { forwardRef, ReactNode, useState } from "react";

import { useTheme } from "@/modules/theme/hooks/useTheme";

import {
  InputErrorMessage,
  InputErrorMessageProps,
} from "../ErrorMessage/InputErrorMessage";
import { InputError } from "../InputError/InputError";
import { Label } from "../Label/Label";

import { createTextInputStyles } from "./helpers/createTextInputStyles";

type ReactInputProps = React.ComponentProps<"input">;

export interface TextInputProps
  extends ReactInputProps,
    InputErrorMessageProps {
  name: string;
  type?:
    | "email"
    | "password"
    | "text"
    | "number"
    | "url"
    | "tel"
    | "date"
    | "time";
  label?: ReactNode;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  showError?: boolean;
  className?: string;
  inputClassName?: string;
  inputWrapperClassName?: string;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      name,
      type,
      label,
      startAdornment,
      endAdornment,
      error,
      errorMessages,
      ErrorMessage,
      disabled,
      className,
      inputClassName,
      inputWrapperClassName,
      ...props
    },
    ref
  ) => {
    const classes = createTextInputStyles(useTheme());
    const [appliedError, setAppliedError] = useState("");

    const handleErrorChange = (error: string) => {
      setAppliedError(error);
    };

    return (
      <div className={cx(classes.root, className)}>
        {label && <Label htmlFor={name}>{label}</Label>}
        <div
          className={cx(
            classes.wrapper,
            appliedError ? classes.wrapperError : classes.wrapperRegular,
            { [classes.startAdornment]: Boolean(startAdornment) },
            { [classes.endAdornment]: Boolean(endAdornment) },
            { [classes.disabled]: disabled },
            inputWrapperClassName
          )}
        >
          <div
            className={cx(
              classes.adornmentWrapper,
              appliedError && classes.adornmentWrapperError
            )}
          >
            {startAdornment}
          </div>
          <input
            ref={ref}
            id={name}
            name={name}
            type={type}
            disabled={disabled}
            className={cx(
              classes.input,
              inputClassName,
              appliedError && classes.error
            )}
            {...props}
          />
          <div
            className={cx(
              classes.adornmentWrapper,
              appliedError && classes.adornmentWrapperError
            )}
          >
            {endAdornment}
          </div>
        </div>
        <InputError>
          <InputErrorMessage
            error={error}
            errorMessages={errorMessages}
            ErrorMessage={ErrorMessage}
            name={name}
            onErrorChange={handleErrorChange}
          />
        </InputError>
      </div>
    );
  }
);
